/**
 * Recherche texte de la marketplace traduite en filtres : « Porsche Cayenne E4 Electric » pose les filtres
 * Porsche et Cayenne E4, comme dans « Filtrer », et le texte reste dans le champ. La règle est celle du serveur
 * (VehicleMake::resolveSearchQuery) : quand elle trouve une marque, la page recharge l'adresse de la recherche,
 * que le serveur redirige vers les filtres.
 *
 * Le texte et ses filtres vont ensemble : effacer le texte retire ses filtres, retirer l'un de ses filtres
 * efface le texte, une nouvelle recherche remplace l'ancienne. Un filtre choisi à la main n'est jamais touché.
 */
import type { InstantSearch } from "instantsearch.js";
import { forgetSearchFiltersOrigin, readSearchFiltersOrigin, rememberSearchFiltersOrigin } from "@/shared/search-filters-origin";

type SearchQueryResolution = { make: string | null; model: string | null };

type IndexUiState = {
    query?: string;
    page?: number;
    menu?: Record<string, string>;
    refinementList?: Record<string, string[]>;
};

type HelperLike = {
    on(event: "change", cb: () => void): void;
};

/** Filtres posés par une recherche : on ne retire que ceux-là. */
type LinkedFilters = { query: string; make: string; model: string | null };

async function fetchResolution(search: string): Promise<SearchQueryResolution | null> {
    try {
        const response = await fetch(`/marketplace/resolve-query${search}`, {
            headers: { Accept: "application/json" },
        });
        return response.ok ? ((await response.json()) as SearchQueryResolution) : null;
    } catch {
        return null;
    }
}

/**
 * Branche le lien texte ↔ filtres et renvoie le contrôle à faire avant de chercher (texte vide compris). Il
 * renvoie true quand il a tout réglé lui-même : départ direct vers la recherche filtrée, ou une seule recherche
 * qui retire en même temps les filtres de la recherche précédente. Sinon la recherche part telle quelle.
 *
 * `toUrl` : adresse /marketplace d'un état de la recherche (uiStateToMarketplaceUrl), prise sur l'état en cours
 * et non sur l'URL du navigateur, que le routeur n'écrit qu'avec un délai.
 */
export function initSearchQueryFilters(
    search: InstantSearch,
    indexName: string,
    helper: HelperLike | null,
    toUrl: (uiState: Record<string, IndexUiState>) => string,
): (query: string) => Promise<boolean> {
    if (!helper) return () => Promise.resolve(false);

    const indexState = (): IndexUiState => (search.getUiState()[indexName] ?? {}) as IndexUiState;
    const queryOf = (state: IndexUiState): string => (state.query ?? "").trim();
    const modelsOf = (state: IndexUiState): string[] => state.refinementList?.model ?? [];

    // Page ouverte par une recherche traduite (redirection du serveur) : ses filtres sont liés au texte, seulement
    // s'ils sont bien ceux que la recherche a posés
    const initial = indexState();
    const origin = readSearchFiltersOrigin();
    let linked: LinkedFilters | null = null;
    if (initial.menu?.make && origin && origin.query === queryOf(initial) && queryOf(initial) !== ""
        && (origin.make === null || origin.make === initial.menu.make)
        && (origin.model === null || modelsOf(initial).includes(origin.model))) {
        const models = modelsOf(initial);
        // Résolution connue : son modèle seul (aucun si elle n'en a posé). Recherche venue d'une autre page :
        // le serveur n'a posé un modèle que s'il était le seul
        const model = origin.make !== null ? origin.model : (models.length === 1 ? models[0] : null);
        linked = { query: queryOf(initial), make: initial.menu.make, model };
    } else if (origin) {
        forgetSearchFiltersOrigin();
    }
    let lastQuery = queryOf(initial);

    const unlink = () => {
        linked = null;
        forgetSearchFiltersOrigin();
    };

    /** État de la recherche sans la marque ni le modèle posés par la recherche liée. */
    const withoutLinked = (state: IndexUiState, link: LinkedFilters | null): IndexUiState => {
        const menu = { ...(state.menu ?? {}) };
        const refinementList = { ...(state.refinementList ?? {}) };
        if (link) {
            if (menu.make === link.make) {
                delete menu.make;
                // Le modèle dépend de la marque : il part avec elle
                delete refinementList.model;
            } else if (link.model) {
                const models = (refinementList.model ?? []).filter((model) => model !== link.model);
                if (models.length > 0) refinementList.model = models;
                else delete refinementList.model;
            }
        }
        return { ...state, page: 1, menu, refinementList };
    };

    const resolve = (query: string) => {
        const current = indexState();
        if (current.menu?.make || modelsOf(current).length > 0) return;

        // État au moment de la recherche : tout choix fait ensuite (filtre, tri) annule la traduction
        const before = JSON.stringify(current);
        void (async () => {
            const url = toUrl({ ...search.getUiState(), [indexName]: withoutLinked(current, null) });
            const resolution = await fetchResolution(url.slice(url.indexOf("?")));
            if (!resolution?.make || JSON.stringify(indexState()) !== before) return;
            rememberSearchFiltersOrigin(query, resolution.make, resolution.model);
            window.location.assign(url);
        })();
    };

    // Textes déjà vérifiés avant la recherche : le contrôle d'après ne les refait pas
    const checkedBeforeCommit = new Set<string>();
    // Seule la dernière recherche envoyée compte (Entrée deux fois de suite)
    let latest = 0;
    // Départ déjà lancé vers une recherche filtrée : une recherche envoyée ensuite part à sa place
    let leaving = false;

    const resolveBeforeCommit = async (rawQuery: string): Promise<boolean> => {
        const query = rawQuery.trim();
        const token = ++latest;
        const link = linked;
        const next = { ...withoutLinked(indexState(), link), query };
        const nextUiState = { ...search.getUiState(), [indexName]: next };

        /** Une seule recherche : le nouveau texte, sans les filtres de la recherche précédente. */
        const replaceLinked = (): boolean => {
            if (!link) return false;
            unlink();
            lastQuery = query;
            search.setUiState(nextUiState);
            return true;
        };

        if (leaving) {
            // Le serveur traduit à son tour cette recherche s'il le faut
            forgetSearchFiltersOrigin();
            if (query !== "") rememberSearchFiltersOrigin(query);
            window.location.assign(toUrl(nextUiState));
            return true;
        }

        // Texte vide, ou marque / modèle choisis à la main : rien à traduire
        if (query === "" || next.menu?.make || modelsOf(next).length > 0) return replaceLinked();

        checkedBeforeCommit.add(query);
        const url = toUrl(nextUiState);
        // Tout choix fait pendant l'attente (filtre, tri) l'emporte : la recherche part alors telle quelle
        const before = JSON.stringify(indexState());
        const resolution = await fetchResolution(url.slice(url.indexOf("?")));
        if (token !== latest) return true;
        if (JSON.stringify(indexState()) !== before) return false;
        if (!resolution?.make) return replaceLinked();

        // Le serveur redirige vers la même recherche avec ses filtres : un seul chargement, pas d'état intermédiaire
        rememberSearchFiltersOrigin(query, resolution.make, resolution.model);
        leaving = true;
        window.location.assign(url);
        return true;
    };

    helper.on("change", () => {
        const state = indexState();
        const query = queryOf(state);
        const queryChanged = query !== lastQuery;
        lastQuery = query;

        if (linked) {
            const link = linked;
            if (queryChanged) {
                // Texte changé hors de la barre (Retour, autre champ) : ses filtres partent avec lui
                unlink();
                setTimeout(() => {
                    search.setUiState((uiState) => ({ ...uiState, [indexName]: withoutLinked((uiState[indexName] ?? {}) as IndexUiState, link) }));
                    if (query !== "" && !checkedBeforeCommit.has(query)) resolve(query);
                }, 0);
                return;
            }
            const makeRemoved = state.menu?.make !== link.make;
            const modelRemoved = link.model !== null && !modelsOf(state).includes(link.model);
            if (makeRemoved || modelRemoved) {
                // Un filtre de la recherche retiré à la main : le texte part aussi, l'autre filtre reste choisi
                unlink();
                lastQuery = "";
                setTimeout(() => search.setUiState((uiState) => ({
                    ...uiState,
                    [indexName]: makeRemoved
                        ? { ...withoutLinked((uiState[indexName] ?? {}) as IndexUiState, link), query: "" }
                        : { ...((uiState[indexName] ?? {}) as IndexUiState), page: 1, query: "" },
                })), 0);
            }
            return;
        }

        // Recherche lancée sans le contrôle d'avant (champ de recherche d'ordinateur) : traduite après coup
        if (queryChanged && query !== "" && !checkedBeforeCommit.has(query)) resolve(query);
    });

    return resolveBeforeCommit;
}
